import styled from "styled-components";
import { MediaQuery } from "../../hooks/useDeviceType";
import {
  CULTURED,
  EERIE_BLACK,
  GAINSBORO,
  PRIMARY_SIZE,
  SECONDARY_FONT,
} from "../../utils/variables";

export const Container = styled.header`
  display: flex;
  justify-content: space-between;
  padding: 3rem 4rem;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 30;
  background-color: ${(props) =>
    props.$isTop ? "transparent" : "rgba(9, 11, 14, 0.78)"};
  -webkit-backdrop-filter: ${(props) =>
    props.$isTop ? "none" : "blur(1.6rem)"};
  backdrop-filter: ${(props) => (props.$isTop ? "none" : "blur(1.6rem)")};
  transform: ${(props) =>
    props.$showHeader ? "translateY(0)" : "translateY(-100%)"};
  transition: transform 0.25s ease-out, box-shadow 0.25s ease-out,
    background-color 0.25s ease-out;

  -webkit-box-shadow: ${(props) =>
    !props.$isTop ? "0px 10px 14px 0px rgba(0, 0, 0, 0.1)" : "unset"};
  -moz-box-shadow: ${(props) =>
    !props.$isTop ? "0px 10px 14px 0px rgba(0, 0, 0, 0.1)" : "unset"};
  box-shadow: ${(props) =>
    !props.$isTop ? "0px 10px 14px 0px rgba(0, 0, 0, 0.1)" : "unset"};

  @media ${MediaQuery.isTablet} {
    padding: 2.8rem 4rem;
  }
`;

export const LogoContainer = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  font-family: ${SECONDARY_FONT};
  font-size: 2.6rem;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${GAINSBORO};
    outline-offset: 0.5rem;
    border-radius: 0.4rem;
  }

  img {
    width: 10rem;
    height: auto;
  }
`;

export const Navigation = styled.nav`
  display: flex;
  align-items: center;

  @media ${MediaQuery.isTablet} {
    flex-direction: column;
    padding: 8rem 4rem;
    -webkit-transform: scaleX(-1);
    transform: scaleX(-1);

    a {
      color: ${EERIE_BLACK};
      border-color: ${EERIE_BLACK};
      background-position: right;
      margin-top: 4rem;
    }
  }

  @media ${MediaQuery.isMobile} {
    flex-direction: column;
    padding: 8rem 3rem;
    -webkit-transform: scaleX(-1);
    transform: scaleX(-1);

    a {
      color: ${EERIE_BLACK};
      border-color: ${EERIE_BLACK};
      background-position: right;
      margin-top: 4rem;
    }
  }
`;

export const Links = styled.ul`
  display: flex;
  list-style-type: none;
  align-items: center;

  @media ${MediaQuery.isTablet} {
    flex-direction: column;
    align-items: flex-start;
  }

  @media ${MediaQuery.isMobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Link = styled.li`
  font-family: ${SECONDARY_FONT};
  color: ${CULTURED};
  font-size: 1.3rem;
  margin: 1rem 2rem;

  @media ${MediaQuery.isTablet} {
    color: ${EERIE_BLACK};
    font-size: ${PRIMARY_SIZE};
    margin: 2rem 0;
  }

  @media ${MediaQuery.isMobile} {
    color: ${EERIE_BLACK};
    font-size: ${PRIMARY_SIZE};
    margin: 2rem 0;
    font-size: 1.6rem;
  }

  a {
    color: ${GAINSBORO};
    text-decoration: none;
    margin-left: 0.8rem;

    &:hover {
      color: ${CULTURED};
    }

    @media ${MediaQuery.isTablet} {
      color: ${EERIE_BLACK};

      &:hover {
        color: unset;
      }
    }

    @media ${MediaQuery.isMobile} {
      color: ${EERIE_BLACK};

      &:hover {
        color: unset;
      }
    }
  }
`;

export const SidebarFooter = styled.div`
  width: 100%;
  margin-top: auto;
  padding-top: 3rem;
  text-align: center;
  border-top: 1px solid rgba(218, 221, 216, 0.1);
`;

export const SidebarSocials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const SidebarSocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4.2rem;
  height: 4.2rem;
  margin: 0;
  color: ${GAINSBORO};
  border: 1px solid rgba(218, 221, 216, 0.18);
  border-radius: 0.8rem;
  transition: color 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease, transform 0.2s ease;

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }

  &:hover,
  &:focus-visible {
    color: ${CULTURED};
    border-color: rgba(218, 221, 216, 0.42);
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-0.2rem);
  }
`;

export const SidebarEmail = styled.a`
  display: inline-block;
  margin: 0;
  color: ${GAINSBORO};
  font-family: ${SECONDARY_FONT};
  font-size: 1.2rem;
  text-decoration: none;
  letter-spacing: 0.04rem;

  &:hover,
  &:focus-visible {
    color: ${CULTURED};
  }
`;

export const SidebarContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: ${(props) => (props.$isOpen ? "100vw" : "8rem")};
  height: 100dvh;
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
`;

export const SidebarBackdrop = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  border: none;
  background: rgba(2, 4, 7, 0.62);
  -webkit-backdrop-filter: blur(0.4rem);
  backdrop-filter: blur(0.4rem);
  cursor: pointer;
`;

export const Sidebar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: min(84vw, 32rem);
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  pointer-events: auto;
  background:
    radial-gradient(
      circle at 90% 8%,
      rgba(74, 133, 143, 0.18),
      transparent 32%
    ),
    rgba(9, 11, 14, 0.97);
  border-left: 1px solid rgba(218, 221, 216, 0.12);
  box-shadow: -2rem 0 6rem rgba(0, 0, 0, 0.42);

  ${Navigation} {
    width: 100%;
    height: 100%;
    align-items: flex-start;
    padding: 12rem 4rem 4rem;
    transform: none;
  }

  ${SidebarSocialLink},
  ${SidebarEmail} {
    margin-top: 0;
    color: ${GAINSBORO};
  }

  ${Links} {
    width: 100%;
    align-items: flex-start;
  }

  ${Link} {
    width: 100%;
    color: ${GAINSBORO};
    margin: 0;
    padding: 1.6rem 0;
    border-bottom: 1px solid rgba(218, 221, 216, 0.1);

    a {
      color: ${CULTURED};
      margin-left: 1rem;

      &:hover,
      &:focus-visible {
        color: ${GAINSBORO};
      }
    }
  }
`;

export const MenuButton = styled.button`
  outline: none;
  border: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  cursor: pointer;
  pointer-events: auto;
  position: absolute;
  z-index: 3;
  top: 16px;
  right: 16px;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${GAINSBORO};
  border-radius: 1.4rem;
  background: transparent;
  box-shadow: none;
  transition: color 0.2s ease, opacity 0.2s ease, transform 0.16s ease;

  &:hover {
    opacity: 0.72;
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${GAINSBORO};
    outline-offset: 0.3rem;
  }
`;
